// @hssna/family-web 路由定义
// 规格: SPEC §2.4 / §8.1 family-web 功能规格 / ADR-0011 (React Router 6 Data Router)
// basename: /app (对齐 nginx path-prefix, 在 main.tsx 中配置)
//
// 路由表 (T2-01):
//   /login                      登录页 (未认证可访问)
//   /                           RequireAuth 包裹的 AppLayout
//     index                     首页 (态势概览)
//     /members                  家庭成员管理 (T2-02)
//     /devices                  设备管理 (T2-03)
//     /privacy                  隐私控制台 (T2-04)
//     /consent                  授权管理 (T2-05)
//     /consent/proxy            代理决策 (T2-06)
//     /consent/minor            未成年人保护 (T2-07)
//     /export                   数据导出 (T2-08)
//     /alerts                   告警历史 (T2-09)
//     /alert-preferences        告警分级接收设置 (T4-05)
//     /ems-preauth              120 代呼预授权 (T4-06)
//     /health-timeline          健康时间线 (T4-09)
//   *                           404

import { lazy, Suspense, useEffect } from 'react';
import type { ReactNode } from 'react';
import { createBrowserRouter, useNavigate, type RouteObject } from 'react-router-dom';
import { LoginPage } from '@hssna/ui-kit';
import { HomePage } from '../pages/HomePage';
import { AppLayout } from '../components/AppLayout';
import { RequireAuth } from './RequireAuth';
import { useAuthStore } from '../store/auth-store';

// T2-02 ~ T2-09 占位页面: lazy 加载 (将在后续任务中实现)
const FamilyMembersPage = lazy(() =>
  import('../pages/FamilyMembersPage').then((m) => ({ default: m.FamilyMembersPage })),
);
const DevicesPage = lazy(() =>
  import('../pages/DevicesPage').then((m) => ({ default: m.DevicesPage })),
);
const PrivacyConsolePage = lazy(() =>
  import('../pages/PrivacyConsolePage').then((m) => ({ default: m.PrivacyConsolePage })),
);
const ConsentManagementPage = lazy(() =>
  import('../pages/ConsentManagementPage').then((m) => ({ default: m.ConsentManagementPage })),
);
const ProxyDecisionPage = lazy(() =>
  import('../pages/ProxyDecisionPage').then((m) => ({ default: m.ProxyDecisionPage })),
);
const MinorProtectionPage = lazy(() =>
  import('../pages/MinorProtectionPage').then((m) => ({ default: m.MinorProtectionPage })),
);
const DataExportPage = lazy(() =>
  import('../pages/DataExportPage').then((m) => ({ default: m.DataExportPage })),
);
const AlertCenterPage = lazy(() =>
  import('../pages/AlertCenterPage').then((m) => ({ default: m.AlertCenterPage })),
);
const AlertHistoryPage = lazy(() =>
  import('../pages/AlertHistoryPage').then((m) => ({ default: m.AlertHistoryPage })),
);
// T4-05 / T4-06 / T4-09: 战役 4 新增页面 (lazy 加载)
const AlertPreferencesPage = lazy(() =>
  import('../pages/AlertPreferencesPage').then((m) => ({ default: m.AlertPreferencesPage })),
);
const EmsPreAuthorizationPage = lazy(() =>
  import('../pages/EmsPreAuthorizationPage').then((m) => ({ default: m.EmsPreAuthorizationPage })),
);
const HealthTimelinePage = lazy(() =>
  import('../pages/HealthTimelinePage').then((m) => ({ default: m.HealthTimelinePage })),
);
const NotFoundPage = lazy(() =>
  import('../pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);

/** lazy 页面加载占位 */
function PageSuspense({ children }: { readonly children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center p-12 text-gray-500">加载中...</div>
      }
    >
      {children}
    </Suspense>
  );
}

/** 登录页包装: 注入 authClient, 登录成功后写入 store 并跳转首页 */
function LoginPageWrapper() {
  const authClient = useAuthStore((s) => s.authClient);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isInitializing = useAuthStore((s) => s.isInitializing);
  const setTokens = useAuthStore((s) => s.setTokens);
  const navigate = useNavigate();

  // 已认证用户访问 /login → 重定向至首页
  useEffect(() => {
    if (!isInitializing && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isInitializing, isAuthenticated, navigate]);

  if (isInitializing) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-gray-500">正在加载会话...</p>
      </div>
    );
  }

  if (!authClient) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-gray-500">认证客户端未初始化</p>
      </div>
    );
  }

  return (
    <LoginPage
      authClient={authClient}
      onSuccess={(tokens) => {
        setTokens(tokens);
        navigate('/', { replace: true });
      }}
    />
  );
}

export const routes: RouteObject[] = [
  {
    path: '/login',
    element: <LoginPageWrapper />,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <HomePage /> },
      {
        path: 'members',
        element: (
          <PageSuspense>
            <FamilyMembersPage />
          </PageSuspense>
        ),
      },
      {
        path: 'devices',
        element: (
          <PageSuspense>
            <DevicesPage />
          </PageSuspense>
        ),
      },
      {
        path: 'privacy',
        element: (
          <PageSuspense>
            <PrivacyConsolePage />
          </PageSuspense>
        ),
      },
      {
        path: 'consent',
        element: (
          <PageSuspense>
            <ConsentManagementPage />
          </PageSuspense>
        ),
      },
      {
        path: 'consent/proxy',
        element: (
          <PageSuspense>
            <ProxyDecisionPage />
          </PageSuspense>
        ),
      },
      {
        path: 'consent/minor',
        element: (
          <PageSuspense>
            <MinorProtectionPage />
          </PageSuspense>
        ),
      },
      {
        path: 'export',
        element: (
          <PageSuspense>
            <DataExportPage />
          </PageSuspense>
        ),
      },
      {
        path: 'alert-center',
        element: (
          <PageSuspense>
            <AlertCenterPage />
          </PageSuspense>
        ),
      },
      {
        path: 'alerts',
        element: (
          <PageSuspense>
            <AlertHistoryPage />
          </PageSuspense>
        ),
      },
      {
        path: 'alert-preferences',
        element: (
          <PageSuspense>
            <AlertPreferencesPage />
          </PageSuspense>
        ),
      },
      {
        path: 'ems-preauth',
        element: (
          <PageSuspense>
            <EmsPreAuthorizationPage />
          </PageSuspense>
        ),
      },
      {
        path: 'health-timeline',
        element: (
          <PageSuspense>
            <HealthTimelinePage />
          </PageSuspense>
        ),
      },
    ],
  },
  {
    path: '*',
    element: (
      <PageSuspense>
        <NotFoundPage />
      </PageSuspense>
    ),
  },
];

// 已认证用户命中 * (如 /nonexistent) 时, 仍展示 404 (不强制跳转)

export const router = createBrowserRouter(routes, {
  basename: '/app',
});
